import React, { useEffect, useState } from 'react'
import { Col, Form } from 'react-bootstrap'
import { RootState } from '../../store'
import { ParticipantCustomQuestions } from './ParticipantCustomQuestions'
import { useSelector } from 'react-redux'
import { isTabletOrMobileQuery } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import {
  CityField,
  CodeField,
  DateOfBirthField,
  DisabledField,
  DistrictField,
  EmailField,
  EmailRepeatField,
  FirstNameField,
  FormFieldProperties,
  GenderField,
  HandicapField,
  LastNameField,
  LegalGuardianField,
  PhoneField,
  SchoolField,
  SecondLegalGuardianField,
  StreetField,
  ZipField,
} from './ParticipantFormFields'
import {
  FieldErrors,
  FieldValues,
  FormState,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger
} from 'react-hook-form'
import { IParticipant } from '../../store/participant/interfaces'

export const ParticipantMainForm = (props: {
  register: UseFormRegister<FieldValues>
  setValue: UseFormSetValue<FieldValues>
  getValues: UseFormGetValues<FieldValues>
  trigger: UseFormTrigger<FieldValues>
  errors: FieldErrors
  formState: FormState<FieldValues>
  participant: IParticipant  
}) => {
  const {
    errors,
    register,
    setValue,
    getValues,
    trigger,
    formState,
    participant,
  } = props
  const { data: program } = useSelector((state: RootState) => state.program)
  let hiddenOptionalFields: string[] = []
  if (program.hidden_optional_fields) {
    hiddenOptionalFields = program.hidden_optional_fields.split(',')
  }

  const [isDisabled, setIsDisabled] = useState(!!participant.disabled)
  const loadedViaCode = useSelector((state: RootState) => state.misc.loadedViaCode)

  const person = participant
  const formFieldProperties: FormFieldProperties<IParticipant> = {
    formState,
    person,
    errors,
    loadedViaCode,
    register,
  }
  const isTabletOrMobile = useMediaQuery(isTabletOrMobileQuery)

  return (
    <Form
      className={
        isTabletOrMobile
          ? 'border rounded p-3 mb-4'
          : 'border rounded p-3'
      }
    >
      <Form.Row>
        {program.requires_family_data ? (
          <h5>Persönliche Daten eines Elternteils</h5>
        ) : (
          <h5>Persönliche Angaben des Teilnehmers</h5>
        )}
      </Form.Row>
      {isTabletOrMobile ? (
        <>
          <Form.Row>
            <FirstNameField formFieldProperties={formFieldProperties} />
          </Form.Row>
          <Form.Row>
            <LastNameField formFieldProperties={formFieldProperties} />
          </Form.Row>
          {program.gender_selection_needed && (
            <Form.Row>
              <GenderField formFieldProperties={formFieldProperties} />
            </Form.Row>
          )}
          <Form.Row>
            <DateOfBirthField formFieldProperties={formFieldProperties} />
          </Form.Row>
        </>
      ) : (
        <>
          <Form.Row>
            <FirstNameField formFieldProperties={formFieldProperties} />
            <LastNameField formFieldProperties={formFieldProperties} />
          </Form.Row>
          <Form.Row>
            {program.gender_selection_needed && (
              <GenderField formFieldProperties={formFieldProperties} />
            )}
            <DateOfBirthField formFieldProperties={formFieldProperties} />
          </Form.Row>
        </>
      )}
      {hiddenOptionalFields.includes('disabled') ? (
        <></>
      ) : (
        <Form.Row>
          <DisabledField formFieldProperties={formFieldProperties} setShowDisabled={setIsDisabled}/>
        </Form.Row>
      )}
      {isDisabled ? (
        <>
          <Form.Row className='mt-2'>
            <HandicapField formFieldProperties={formFieldProperties}/>
          </Form.Row>
        </>
      ) : (
        <></>
      )}

      <Form.Row className='mt-2'>
        {hiddenOptionalFields.includes('legal_guardian') ? (
          <></>
        ) : (
          <LegalGuardianField formFieldProperties={formFieldProperties} />
        )}
      </Form.Row>
      <Form.Row className='mt-2'>
        {hiddenOptionalFields.includes('second_legal_guardian') ? (
          <></>
        ) : (
          <SecondLegalGuardianField formFieldProperties={formFieldProperties} />
        )}
      </Form.Row>
      {hiddenOptionalFields.includes('school') ? (
        <></>
      ) : (
        <Form.Row>
          <SchoolField formFieldProperties={formFieldProperties} />
        </Form.Row>
      )}
      <Form.Row>
        <h5>Adresse</h5>
      </Form.Row>
      <Form.Row>
        <StreetField formFieldProperties={formFieldProperties} />
      </Form.Row>
      {isTabletOrMobile ? (
        <>
          <Form.Row>
            <ZipField
              formFieldProperties={{ ...formFieldProperties }}
              getValues={getValues}
              setValue={setValue}
            />
          </Form.Row>
          <Form.Row>
            <CityField
              formFieldProperties={formFieldProperties}
            />
          </Form.Row>
          {hiddenOptionalFields.includes('district') ? (
            <></>
          ) : (
            <Form.Row>
              <DistrictField formFieldProperties={formFieldProperties} />
            </Form.Row>
          )}
        </>
      ) : (
        <>
          <Form.Row>
            <ZipField
              formFieldProperties={formFieldProperties}
              getValues={getValues}
              setValue={setValue}
            />
            <CityField
              formFieldProperties={formFieldProperties}
            />
            {hiddenOptionalFields.includes('district') ? (
              <></>
            ) : (
              <Form.Row>
                <DistrictField formFieldProperties={formFieldProperties} />
              </Form.Row>
            )}
          </Form.Row>
        </>
      )}
      <Form.Row>
        <h5>Kontaktdaten</h5>
      </Form.Row>
      <Form.Row>
        <PhoneField formFieldProperties={formFieldProperties} />
      </Form.Row>
      {isTabletOrMobile ? (
        <>
          <Form.Row>
            <EmailField formFieldProperties={formFieldProperties} />
          </Form.Row>
          <Form.Row>
            <EmailRepeatField formFieldProperties={formFieldProperties} />
          </Form.Row>
        </>
      ) : (
        <>
          <Form.Row>
            <EmailField formFieldProperties={formFieldProperties} />
            <EmailRepeatField formFieldProperties={formFieldProperties} />
          </Form.Row>
        </>
      )}
      <ParticipantCustomQuestions
        questions={program.participant_questions}
        errors={errors}
        register={register}
        loadedViaCode={loadedViaCode}
        formState={formState}
        setValue={setValue}
      />
      {program.access_key_mode !== 'off' ? (
        <>
          <Form.Row>
            <h5>Zugangsbeschränkung</h5>
          </Form.Row>
          <Form.Row>
            <CodeField formFieldProperties={formFieldProperties} required={program.access_key_mode === 'req'}/>
          </Form.Row>
        </>
      ) : (
        <></>
      )}
    </Form>
  )
}
